import { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { MentorInboxPage } from "./MentorInboxPage";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface MentorProfile {
  id: string;
  name: string;
  description: string;
}

export default function MentorDeepDivePage() {
  const { t } = useTranslation();
  const PROFILE_UPDATED_EVENT = "innergem:profile-updated";
  const [mentors, setMentors] = useState<MentorProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMentorView, setIsMentorView] = useState(false);
  const [currentMentorId, setCurrentMentorId] = useState<string | null>(null);
  const [subscriptionTier, setSubscriptionTier] = useState<string | null>(null);
  const [hasActiveMentorPass, setHasActiveMentorPass] = useState(false);
  const [mentorMessageCredits, setMentorMessageCredits] = useState<number>(0);
  const [nasTrialActive, setNasTrialActive] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const loadCurrentUserAccess = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        setSubscriptionTier(null);
        setHasActiveMentorPass(false);
        setMentorMessageCredits(0);
        return;
      }

      const { data: profile } = await supabase
        .from("user_profiles")
        .select("role, subscription_tier, trial_code, trial_expires_at, mentor_deepdive_unlimited_until, mentor_message_credits")
        .eq("id", user.id)
        .maybeSingle();

      if (profile && (profile as any).role === "mentor") {
        setIsMentorView(true);
        setCurrentMentorId(user.id);
        return;
      }

      const rawTier = (profile as any)?.subscription_tier as string | null;
      const tier = rawTier ? rawTier.toLowerCase().replace(/\s+/g, "_") : null;
      setSubscriptionTier(tier);

      const rawTrialCode = ((profile as any)?.trial_code as string | null) ?? null;
      const normalizedTrialCode = (rawTrialCode || "").toUpperCase();
      const rawTrialExpiresAt = ((profile as any)?.trial_expires_at as string | null) ?? null;
      const trialExpiryMs = rawTrialExpiresAt ? new Date(rawTrialExpiresAt).getTime() : NaN;
      const isNasTrialActive = normalizedTrialCode === "NAS@INNERGEM30" && (!rawTrialExpiresAt || Number.isNaN(trialExpiryMs) || trialExpiryMs > Date.now());
      setNasTrialActive(isNasTrialActive);

      const until = (profile as any)?.mentor_deepdive_unlimited_until as string | null;
      if (until) {
        const untilDate = new Date(until);
        setHasActiveMentorPass(Number.isFinite(untilDate.getTime()) && untilDate.getTime() > Date.now());
      } else {
        setHasActiveMentorPass(false);
      }

      const credits = (profile as any)?.mentor_message_credits as number | null;
      setMentorMessageCredits(credits ?? 0);
    };

    const loadMentorList = async () => {
      const { data, error } = await supabase
        .from("user_profiles")
        .select("id, full_name, role, mentor_description")
        .eq("role", "mentor");

      if (error) {
        console.error("Failed to load mentors", error);
        setMentors([]);
        return;
      }

      const defaultDescription = t("mentor.defaultDescription", {
        defaultValue:
          "Inner Gem dedicated mentors. They listen to your questions, guide you inward, and help you rediscover inner calm and answers.",
      });

      const fallbackName = t("mentor.defaultName", {
        defaultValue: "Mentor",
      });

      const mapped: MentorProfile[] = (data || []).map((row: any) => {
        const fullName = (row.full_name as string | undefined)?.trim();
        const displayName = fullName && fullName.length > 0 ? fullName : fallbackName;

        const customDescription = (row.mentor_description as string | undefined)?.trim();

        return {
          id: row.id as string,
          name: displayName,
          description: customDescription || defaultDescription,
        };
      });

      setMentors(mapped);
    };

    const loadAll = async () => {
      try {
        await loadCurrentUserAccess();
        await loadMentorList();
      } finally {
        setIsLoading(false);
      }
    };

    const onProfileUpdated = () => {
      void loadCurrentUserAccess();
    };

    loadAll();
    window.addEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
    return () => {
      window.removeEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
    };
  }, [t]);

  const isBeginnerTier = () => {
    if (!subscriptionTier) return false;
    const tier = subscriptionTier.toLowerCase();
    return (
      tier === "beginner" ||
      tier === "beginner_monthly" ||
      tier === "beginner_yearly"
    );
  };

  // 如果当前登录者是导师，直接显示导师专用对话收件箱
  if (isMentorView && currentMentorId) {
    return <MentorInboxPage mentorId={currentMentorId} />;
  }

  return (
    <div className="container max-w-4xl mx-auto py-8 px-4">
      <div className="mb-8 text-center">
        <h1 className="text-3xl md:text-4xl font-bold text-primary mb-2">
          {t("nav.mentor", { defaultValue: "Mentor Deep Dive" })}
        </h1>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          {t("comingSoon.mentor.desc", {
            defaultValue:
              "Connect with mentors, including the founder, for thoughtful answers or 1:1 sessions. Before each conversation, the app prepares a concise Gem Summary so we start deeper and save your time. Transparent pricing, kind boundaries, and privacy by design.",
          })}
        </p>
      </div>

      <Card className="mb-8 shadow-[var(--shadow-soft)] bg-white/70 border-purple-100">
        <CardContent className="pt-4 pb-5">
          <p className="text-sm text-muted-foreground whitespace-pre-line">
            {t("mentorDeepDive.offer", {
              defaultValue:
                "When your inner journey needs a real light to guide the way.\n\nSometimes, in the process of self-awareness, your mind hits a wall. When your Daily Gem notes or the AI insights are not enough to untie the knot in your heart, you can invite the resonance of a real soul to help you shift.\n\nMentor Deep Dive offers thoughtful 1:1 written consultations. Browse the mentors below and choose someone who feels aligned. Share the situation you keep looping on, or attach your Gem Report. Within 24 hours, your mentor will send you a personalised deep reply to help you see through the 3D illusion and remember your inner answers.\n\n💎 Single Deep Dive: SGD 35.99/credit\n🌟 Upper Intermediate members enjoy 20% off: SGD 28.99/credit",
            })}
          </p>

          <div className="mt-3 text-base font-semibold">
            {t("mentorDeepDive.availableCredits", {
              count: mentorMessageCredits,
              defaultValue: "Available credits: {{count}}",
            })}
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <p className="text-center text-sm text-muted-foreground">
          {t("common.loading", { defaultValue: "Loading mentors..." })}
        </p>
      ) : mentors.length === 0 ? (
        <p className="text-center text-sm text-muted-foreground">
          {t("mentor.empty")}
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {mentors.map((mentor) => (
            <Card
              key={mentor.id}
              className="shadow-[var(--shadow-soft)] h-[300px] flex flex-col cursor-pointer hover:border-primary/60 hover:shadow-lg transition pb-6"
              onClick={() => {
                if (nasTrialActive) {
                  setShowUpgradeDialog(true);
                  return;
                }

                if (isBeginnerTier()) {
                  if (hasActiveMentorPass) {
                    navigate(`/mentor/${mentor.id}/chat`, {
                      state: { mentorName: mentor.name },
                    });
                    return;
                  }
                  setShowUpgradeDialog(true);
                  return;
                }

                navigate(`/mentor/${mentor.id}/chat`, {
                  state: { mentorName: mentor.name },
                });
              }}
            >
              <CardHeader className="flex flex-row items-center gap-4">
                <Avatar className="h-10 w-10">
                  <AvatarFallback>
                    {mentor.name.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <CardTitle className="text-lg font-semibold">
                    {mentor.name}
                  </CardTitle>
                  <CardDescription>
                    {t("mentor.role.mentor", {
                      defaultValue: "Inner Gem Mentor",
                    })}
                  </CardDescription>
                </div>
              </CardHeader>
              <CardContent className="pt-0 pb-6 flex-1 overflow-y-auto no-scrollbar">
                <p className="text-sm text-muted-foreground whitespace-pre-line">
                  {mentor.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("mentorDeepDive.upgradeRequiredTitle", {
                defaultValue: "Upgrade required to start Mentor Deep Dive",
              })}
            </DialogTitle>
            <DialogDescription>
              {t("mentorDeepDive.upgradeRequiredDesc", {
                defaultValue:
                  "Beginner members can browse mentor profiles, but need an Intermediate or higher membership to start a 1:1 deep dive conversation.",
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowUpgradeDialog(false)}
            >
              {t("common.cancel", { defaultValue: "Not now" })}
            </Button>
            <Button
              type="button"
              onClick={() => {
                setShowUpgradeDialog(false);
                navigate("/settings");
              }}
            >
              {t("mentorDeepDive.upgradeCta", {
                defaultValue: "View membership options",
              })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
